/**
 * Where sign-in goes back to, DOM-free: the screen a learner was heading for when the session ended, kept in
 * sessionStorage (this tab, never a query string), followed once and only if it is a path inside the app. A separate
 * short-lived localStorage copy lets owner-assisted recovery finish in another same-origin tab. Both copies are bound
 * to the beta account id already cached by the session layer; an unbound or different-account intent is discarded.
 */
export const RETURN_KEY = 'li-return-to';
export const RECOVERY_RETURN_KEY = 'li-recovery-return';
export const RETURN_SUBJECT_KEY = 'li-return-subject';
/** A destination older than this is not followed: it belongs to an earlier, abandoned attempt. */
const MAX_AGE_MS = 30 * 60 * 1000;

type Store = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/** Pages that are not a destination: sign-in and the account links themselves, the API, dev prototypes. */
const NOT_A_DESTINATION = new Set(['signin', 'join', 'recover', 'prototype', 'api']);

/**
 * A destination sign-in may return to: a same-origin path (query and hash kept) with a single leading slash,
 * no backslash or control character, no dot-segment even percent-encoded, no encoded slash or backslash, and not
 * sign-in, join, recover or prototype pages themselves. Anything else is null (home).
 */
export function safeReturn(raw: string | null | undefined): string | null {
  if (!raw || !raw.startsWith('/') || raw.startsWith('//') || raw.includes('\\')) return null;
  if (/[\u0000-\u001f\u007f]/.test(raw)) return null;
  const path = raw.split(/[?#]/)[0];
  if (/%2f|%5c/i.test(path)) return null;
  const segs = path.split('/').slice(1);
  for (const seg of segs) {
    let s: string;
    try { s = decodeURIComponent(seg); } catch { return null; }
    if (s === '.' || s === '..' || s.includes('/') || s.includes('\\')) return null;
  }
  if (NOT_A_DESTINATION.has(segs[0].toLowerCase())) return null;
  return raw;
}

function defaultStore(): Store | undefined {
  try { return globalThis.sessionStorage; } catch { return undefined; }
}

function defaultRecoveryStore(): Store | undefined {
  try { return globalThis.localStorage; } catch { return undefined; }
}

/** The beta account id confirmed in this tab; usernames and learner content are never used as a binding. */
function cachedBetaSubject(store: Store | undefined = defaultStore()): string | undefined {
  try {
    const subject = store?.getItem(RETURN_SUBJECT_KEY);
    return subject || undefined;
  } catch { return undefined; }
}

/** Remember the authenticated account in this tab, independently of the device-wide provisional session cache. */
export function rememberReturnSubject(subject: string): void {
  try { defaultStore()?.setItem(RETURN_SUBJECT_KEY, subject); } catch { /* same-tab intent remains unbound */ }
}

/** An ended session cannot bind a future return intent. */
export function forgetReturnSubject(): void {
  try { defaultStore()?.removeItem(RETURN_SUBJECT_KEY); } catch { /* nothing cached */ }
}

/** Keep a safe destination for this tab and, when bound, a short-lived recovery handoff for another tab. */
export function rememberReturn(
  raw: string,
  store: Store | undefined = defaultStore(),
  now = Date.now(),
  subject = cachedBetaSubject(),
): void {
  const safe = safeReturn(raw);
  if (!safe) return;
  const value = JSON.stringify({ path: safe, at: now, ...(subject ? { subject } : {}) });
  try { store?.setItem(RETURN_KEY, value); } catch { /* private mode: home */ }
  try {
    const recoveryStore = defaultRecoveryStore();
    if (subject) recoveryStore?.setItem(RECOVERY_RETURN_KEY, value);
    else recoveryStore?.removeItem(RECOVERY_RETURN_KEY);
  } catch { /* recovery handoff unavailable */ }
}

/**
 * The server's redirect of a signed-out page load carries the destination as `/signin#r=<encoded path>` (a fragment is
 * never sent to a server or proxy). Keep it if it is safe (it is the newer intent, so it replaces an earlier one), and in
 * every case take the fragment out of the address bar. An unsafe or malformed one leaves a kept destination alone.
 */
export function rememberFromHash(
  loc: Pick<Location, 'hash' | 'pathname' | 'search'> = globalThis.location,
  store: Store | undefined = defaultStore(),
  now = Date.now(),
): void {
  if (!loc.hash.startsWith('#r=')) return;
  let raw = '';
  try { raw = decodeURIComponent(loc.hash.slice(3)); } catch { /* malformed: nothing kept */ }
  rememberReturn(raw, store, now);
  try { history.replaceState(history.state, '', loc.pathname + loc.search); } catch { /* no history: it stays */ }
}

/** Forget it without following it (signing out). */
export function forgetReturn(store: Store | undefined = defaultStore()): void {
  try { store?.removeItem(RETURN_KEY); } catch { /* nothing kept */ }
  forgetRecoveryReturn();
}

function forgetRecoveryReturn(store: Store | undefined = defaultRecoveryStore()): void {
  try { store?.removeItem(RECOVERY_RETURN_KEY); } catch { /* nothing kept */ }
}

export interface TakenReturn {
  /** A safe, fresh destination, or null when none can be followed. */
  path: string | null;
  /** True only when a saved entry existed but was malformed, unsafe, or expired. */
  unavailable: boolean;
}

/** Consume the kept destination once and report when a saved intent could not be followed. */
export function takeReturnResult(store: Store | undefined = defaultStore(), now = Date.now(), subject?: string): TakenReturn {
  return takeStoredReturnResult(store, RETURN_KEY, now, subject);
}

/**
 * Recovery first consumes its same-origin browser handoff, then falls back to the current tab's sign-in intent
 * when browser storage is unavailable. Both copies are validated and consumed.
 */
export function takeRecoveryReturnResult(
  now = Date.now(),
  recoveryStore: Store | undefined = defaultRecoveryStore(),
  sameTabStore: Store | undefined = defaultStore(),
  subject?: string,
): TakenReturn {
  const shared = takeStoredReturnResult(recoveryStore, RECOVERY_RETURN_KEY, now, subject);
  const sameTab = takeStoredReturnResult(sameTabStore, RETURN_KEY, now, subject);
  return shared.path || shared.unavailable ? shared : sameTab;
}

/** Drop tab-local and shared intents when a different account signs in or recovers on this device. */
export function forgetReturnForOtherSubject(
  subject: string,
  sameTabStore: Store | undefined = defaultStore(),
  recoveryStore: Store | undefined = defaultRecoveryStore(),
): void {
  forgetIfNotOwned(sameTabStore, RETURN_KEY, subject);
  forgetIfNotOwned(recoveryStore, RECOVERY_RETURN_KEY, subject);
}

function forgetIfNotOwned(store: Store | undefined, key: string, subject: string): void {
  try {
    const raw = store?.getItem(key);
    if (!raw) return;
    const saved = JSON.parse(raw) as { subject?: unknown };
    if (saved.subject !== subject) store?.removeItem(key);
  } catch {
    try { store?.removeItem(key); } catch { /* nothing kept */ }
  }
}

function takeStoredReturnResult(store: Store | undefined, key: string, now: number, subject?: string): TakenReturn {
  let raw: string | null = null;
  try {
    raw = store?.getItem(key) ?? null;
    store?.removeItem(key);
  } catch { /* storage out of reach */ return { path: null, unavailable: false }; }
  if (!raw) return { path: null, unavailable: false };
  try {
    const v = JSON.parse(raw) as { path?: unknown; at?: unknown; subject?: unknown };
    if (typeof v.path !== 'string' || typeof v.at !== 'number' || now - v.at > MAX_AGE_MS || now < v.at)
      return { path: null, unavailable: true };
    // Production callers pass the authenticated account id. Old/unbound records and records for another account
    // are consumed as unavailable, never followed.
    if (typeof subject !== 'string' || !subject || v.subject !== subject) return { path: null, unavailable: true };
    const path = safeReturn(v.path);
    return path ? { path, unavailable: false } : { path: null, unavailable: true };
  } catch {
    return { path: null, unavailable: true };
  }
}

/** The kept destination, once: it is gone after this call, whether it was safe and fresh or not. */
export function takeReturn(store: Store | undefined = defaultStore(), now = Date.now(), subject?: string): string | null {
  const result = takeReturnResult(store, now, subject);
  if (result.path || result.unavailable) forgetRecoveryReturn();
  return result.path;
}
